import { Delete as DeleteIcon } from '@mui/icons-material'
import { Box, IconButton } from '@mui/material'
import { useEffect } from 'react'
import { ArrayPath, FieldArrayWithId, FieldPath, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { FlexBox } from 'components/Box'
import { NumberField, TextField } from 'components/Form'
import { calculateLineItems } from '../lineItems'
import { Schema } from '../schema'

type Props = {
  canUpdate: boolean
  field: FieldArrayWithId<Schema, ArrayPath<Schema>, 'id'>
  index: number
  onRemove: (index: number) => void
}

export const ProposalItemForm = ({ canUpdate, field, index, onRemove }: Props): JSX.Element => {
  const { t } = useTranslation('features/globalSetup', {
    keyPrefix: 'googleAdManagerConnection.setupRetargetingDeliveryItems.form',
  })

  const { control, resetField, trigger, watch } = useFormContext<Schema>()
  const { maxPrice, minPrice, step } = watch(`proposalsData.${index}`)

  const PATH_PREFIX: FieldPath<Schema> = `proposalsData.${index}`
  const MIN_PRICE_PATH: FieldPath<Schema> = `${PATH_PREFIX}.minPrice`
  const MAX_PRICE_PATH: FieldPath<Schema> = `${PATH_PREFIX}.maxPrice`
  const LINE_ITEMS_PATH: FieldPath<Schema> = `${PATH_PREFIX}.lineItems`

  const calculatedLineItems: number = calculateLineItems({
    max: maxPrice,
    min: minPrice,
    step,
  })

  useEffect(() => {
    resetField(LINE_ITEMS_PATH, {
      defaultValue: calculatedLineItems,
      keepError: true,
      keepTouched: true,
    })

    trigger([MIN_PRICE_PATH, MAX_PRICE_PATH, LINE_ITEMS_PATH])
  }, [minPrice, maxPrice, step])

  return (
    <FlexBox
      axis='x'
      key={field.id}
      sx={{
        alignItems: 'baseline',
        gap: 2,
      }}
    >
      <TextField
        control={control}
        disabled={!canUpdate}
        label={index === 0 ? t('proposalIdItem.proposalId.label') : undefined}
        name={`proposalsData.${index}.proposalId`}
        sx={{
          mr: index > 0 ? '28px' : 0, // tooltip width compensation
        }}
        tooltip={index === 0 ? { content: t('proposalIdItem.proposalId.tooltip') } : undefined}
      />

      <NumberField
        control={control}
        disabled={!canUpdate}
        label={index === 0 ? t('proposalIdItem.minPrice.label') : undefined}
        name={`proposalsData.${index}.minPrice`}
        width='sm'
      />
      {'-'}
      <NumberField
        control={control}
        disabled={!canUpdate}
        label={index === 0 ? t('proposalIdItem.maxPrice.label') : undefined}
        name={`proposalsData.${index}.maxPrice`}
        width='sm'
      />

      <NumberField
        control={control}
        disabled={!canUpdate}
        label={index === 0 ? t('proposalIdItem.step.label') : undefined}
        name={`proposalsData.${index}.step`}
        width='sm'
      />

      <NumberField
        control={control}
        disabled
        label={index === 0 ? t('proposalIdItem.lineItems.label') : undefined}
        name={`proposalsData.${index}.lineItems`}
        width='sm'
      />

      <Box
        sx={{
          alignSelf: 'flex-start',
          display: 'flex',
          pt: 1,
        }}
      >
        <IconButton onClick={(): void => onRemove(index)}>
          <DeleteIcon />
        </IconButton>
      </Box>
    </FlexBox>
  )
}
